import type { UserFeatureState } from '@eventbrite/organizer-profile-model';
import {
  type QueryFunctionContext,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

import { withBasePath } from '@/utils/withBasePath';

const FEATURE_KEY = 'featured_event_pin_tooltip_v1';
const QUERY_KEY = ['user-feature-state', FEATURE_KEY] as const;

type FeatureState = Partial<UserFeatureState>;

interface PinnedEvent {
  organizerId: string;
  eventId: string;
}

async function fetchFeatureState({
  signal,
}: QueryFunctionContext<typeof QUERY_KEY>): Promise<FeatureState> {
  const response = await fetch(withBasePath(`/api/me/feature-state/${FEATURE_KEY}`), {
    credentials: 'include',
    cache: 'no-store',
    signal,
  });

  if (!response.ok) {
    throw new Error(`Unable to load the featured-event tooltip state (${response.status})`);
  }

  return (await response.json()) as FeatureState;
}

async function markFeatureAsPinned({ organizerId, eventId }: PinnedEvent): Promise<void> {
  const response = await fetch(withBasePath(`/api/me/feature-state/${FEATURE_KEY}`), {
    method: 'PUT',
    credentials: 'include',
    cache: 'no-store',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      action: 'pinned',
      metadata: { organizerId, eventId },
    }),
  });

  if (!response.ok) {
    throw new Error(`Unable to save the featured-event tooltip state (${response.status})`);
  }
}

export function useFeaturedEventPinTooltip(enabled: boolean) {
  const queryClient = useQueryClient();
  const query = useQuery<FeatureState, Error, FeatureState, typeof QUERY_KEY>({
    queryKey: QUERY_KEY,
    queryFn: fetchFeatureState,
    enabled,
    retry: false,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });
  const mutation = useMutation({
    mutationFn: markFeatureAsPinned,
    onMutate: async ({ organizerId, eventId }) => {
      await queryClient.cancelQueries({ queryKey: QUERY_KEY });
      queryClient.setQueryData<FeatureState>(QUERY_KEY, (previous = {}) => ({
        ...previous,
        action: 'pinned',
        lastActionedAt: new Date().toISOString(),
        metadata: { organizerId, eventId },
      }));
    },
    onError: (error) => {
      // Pinning the event succeeded already. Keep the education hidden for this
      // session even if persisting its cross-device state fails.
      console.error('[featured-event] Unable to persist pin tooltip state:', error);
    },
  });

  return {
    shouldShowTooltip: enabled && query.isSuccess && !query.data?.action,
    markPinned: mutation.mutate,
  };
}
